// NotificationBell.js - Notification bell icon with unread count
import React, { useState } from 'react';
import { Bell, CheckCheck, Settings } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationPanel from './NotificationPanel';

const NotificationBell = () => {
  const {
    unreadCount,
    markAllAsRead,
    pushSupported,
    pushSubscribed,
    subscribeToPush,
    unsubscribeFromPush,
    requestNotificationPermission
  } = useNotifications();
  
  const [showPanel, setShowPanel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const handleBellClick = () => {
    setShowPanel(!showPanel);
    setShowSettings(false);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handlePushToggle = async () => {
    if (pushSubscribed) {
      await unsubscribeFromPush();
    } else {
      // Request permission first
      const hasPermission = await requestNotificationPermission();
      if (hasPermission) {
        await subscribeToPush();
      } else {
        alert('Please enable notifications in your browser settings to receive push notifications.');
      }
    }
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={handleBellClick}
        className="relative p-2 text-videbits-600 hover:bg-videbits-50 hover:text-videbits-800 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:ring-offset-2 rounded-full"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-5">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {showPanel && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-sm text-videbits-600 hover:text-videbits-800 flex items-center space-x-1"
                  title="Mark all as read"
                >
                  <CheckCheck className="h-4 w-4" />
                  <span>Mark all read</span>
                </button>
              )}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="text-videbits-500 hover:text-videbits-700"
                title="Notification settings"
              >
                <Settings className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Notification Settings</h4>
              
              {/* Push Notifications Toggle */}
              {pushSupported && (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-700">Push Notifications</span>
                    <p className="text-xs text-gray-500">Get notified even when the app is closed</p>
                  </div>
                  <button
                    onClick={handlePushToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      pushSubscribed 
                        ? 'bg-videbits-600' 
                        : 'bg-videbits-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        pushSubscribed ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}

              {!pushSupported && (
                <p className="text-xs text-gray-500">
                  Push notifications are not supported in your browser.
                </p>
              )}
            </div>
          )}

          {/* Notifications List */}
          <NotificationPanel onClose={() => setShowPanel(false)} />
        </div>
      )}

      {/* Backdrop */}
      {showPanel && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowPanel(false)}
        />
      )}
    </div>
  );
};

export default NotificationBell;
